import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Card,
  ErrorNote,
  Loading,
  Select,
  Stat,
  StatusBadge,
  Table,
  Td,
  Th,
} from '../components/ui';
import { FunnelBars } from './CampaignDetail';
import type { Campaign, Engagement, Funnel, RoiRow, TimelinePoint } from '../lib/api';
import { date, money, percent } from '../lib/format';
import { useApi } from '../lib/hooks';

interface WinLossData {
  by_outcome: Array<{ outcome: string; count: number; total_value: number }>;
  reasons: Array<{ outcome: string; reason: string; prospect: string; closed_at: string; prospect_id: string }>;
  top_objections: Array<{ objection: string; count: number }>;
}

interface SentimentData {
  total: number;
  avg_sentiment: number | null;
  by_intent: Array<{ intent: string; count: number; avg_sentiment: number | null }>;
}

export function Analytics() {
  const [campaignId, setCampaignId] = useState('');
  const [days, setDays] = useState('30');

  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');

  const suffix = useMemo(() => (campaignId ? `?campaign_id=${campaignId}` : ''), [campaignId]);
  const timelineQuery = useMemo(
    () => `/api/analytics/timeline?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ''}`,
    [days, campaignId],
  );

  const funnel = useApi<Funnel>(`/api/analytics/funnel${suffix}`);
  const engagement = useApi<Engagement>(`/api/analytics/engagement${suffix}`);
  const roi = useApi<{ rows: RoiRow[] }>(`/api/analytics/roi${suffix}`);
  const winLoss = useApi<WinLossData>(`/api/analytics/win-loss${suffix}`);
  const sentiment = useApi<SentimentData>(`/api/analytics/sentiment${suffix}`);
  const timeline = useApi<{ points: TimelinePoint[] }>(timelineQuery);

  if (funnel.loading) return <Loading label="Crunching numbers" />;
  if (funnel.error) return <ErrorNote message={funnel.error} onRetry={funnel.reload} />;
  if (!funnel.data) return null;

  const totalWonValue = roi.data?.rows.reduce((sum, row) => sum + row.won_value, 0) ?? 0;
  const totalPipeline = roi.data?.rows.reduce((sum, row) => sum + row.pipeline_value, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Funnel, engagement, and where the revenue actually came from."
        helpHref="analytics"
        actions={
          <>
            <div className="w-56">
              <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
                <option value="">All campaigns</option>
                {campaigns.data?.campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-36">
              <Select value={days} onChange={(event) => setDays(event.target.value)}>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </Select>
            </div>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Prospects" value={funnel.data.prospects} sub={`${funnel.data.contacted} contacted`} />
        <Stat label="Reply rate" value={percent(funnel.data.reply_rate, 1)} sub={`${funnel.data.replied} replies`} />
        <Stat label="Open rate" value={percent(engagement.data?.open_rate)} sub={`${engagement.data?.sent ?? 0} emails sent`} />
        <Stat label="Won" value={money(totalWonValue)} tone="positive" sub={`${funnel.data.won} deals`} />
        <Stat
          label="Open pipeline"
          value={money(totalPipeline)}
          sub="estimated deal value"
          tip="Total value of open deals, plus any value typed on a prospect that has no deal yet."
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Funnel">
          <FunnelBars funnel={funnel.data} />
        </Card>

        <Card title={`Activity over ${days} days`}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timeline.data?.points ?? []}
                margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke="#e6e9ef" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11, fill: '#8592ab' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: string) => date(value).replace(/,.*/, '')}
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#8592ab' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #e6e9ef',
                    fontSize: 12,
                  }}
                  labelFormatter={(value: string) => date(value)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent" name="Sent" fill="#337dff" radius={[2, 2, 0, 0]} />
                <Bar dataKey="replies" name="Replies" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                <Bar dataKey="won" name="Won" fill="#10b981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="By campaign" className="mt-4" bodyClassName="p-0">
        <Table>
          <thead>
            <tr>
              <Th>Campaign</Th>
              <Th>Prospects</Th>
              <Th>Sent</Th>
              <Th>Replies</Th>
              <Th>Won</Th>
              <Th>Lost</Th>
              <Th>Won value</Th>
              <Th tip="Deals opened for this campaign, how many were won, and how many are still open (value in the Open pipeline card).">
                Deals
              </Th>
              <Th tip="Won value divided by total prospects in the campaign — a blended per-prospect return.">
                Value / prospect
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(roi.data?.rows ?? []).map((row) => (
              <tr key={row.campaign_id ?? 'none'} className="hover:bg-ink-50/60">
                <Td className="font-medium">{row.campaign_name}</Td>
                <Td className="tabular-nums">{row.prospects}</Td>
                <Td className="tabular-nums">{row.sent}</Td>
                <Td className="tabular-nums">
                  {row.replies}
                  {row.sent > 0 && (
                    <span className="ml-1.5 text-xs text-ink-500">{percent(row.replies / row.sent)}</span>
                  )}
                </Td>
                <Td className="tabular-nums text-emerald-700">{row.won}</Td>
                <Td className="tabular-nums text-ink-500">{row.lost}</Td>
                <Td className="tabular-nums font-medium">{money(row.won_value)}</Td>
                <Td className="tabular-nums text-ink-600">
                  {row.deals_created}
                  <span className="ml-1.5 text-xs text-ink-500">
                    {row.deals_won} won · {row.open_deals} open
                  </span>
                </Td>
                <Td className="tabular-nums">{money(row.value_per_prospect)}</Td>
              </tr>
            ))}
            {(roi.data?.rows ?? []).length === 0 && (
              <tr>
                <Td className="py-6 text-center text-ink-500">No campaign data yet.</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Card title="Sentiment" className="mt-4" bodyClassName="p-0">
        {sentiment.data?.total ? (
          <div className="p-4">
            <p className="mb-3 text-sm text-ink-600">
              {sentiment.data.total} replies analyzed by Claude, average sentiment{' '}
              <span className="font-medium text-ink-900">
                {sentiment.data.avg_sentiment != null ? sentiment.data.avg_sentiment.toFixed(2) : '—'}
              </span>{' '}
              (-1 negative to +1 positive).
            </p>
            <ul className="space-y-2">
              {sentiment.data.by_intent.map((row) => (
                <li key={row.intent} className="flex items-center justify-between gap-3">
                  <StatusBadge status={row.intent} />
                  <span className="flex-1 border-b border-dotted border-ink-200" />
                  <span className="text-sm tabular-nums text-ink-800">{row.count}</span>
                  <span className="w-16 text-right text-sm tabular-nums text-ink-500">
                    {row.avg_sentiment != null ? row.avg_sentiment.toFixed(2) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-ink-500">
            No sentiment data yet — it fills in as replies are logged or synced from Gmail.
          </p>
        )}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Outcomes">
          {winLoss.data?.by_outcome.length ? (
            <ul className="space-y-2">
              {winLoss.data.by_outcome.map((outcome) => (
                <li key={outcome.outcome} className="flex items-center justify-between gap-3">
                  <StatusBadge status={outcome.outcome} />
                  <span className="flex-1 border-b border-dotted border-ink-200" />
                  <span className="text-sm tabular-nums text-ink-800">{outcome.count}</span>
                  {outcome.total_value > 0 && (
                    <span className="w-24 text-right text-sm tabular-nums text-emerald-700">
                      {money(outcome.total_value)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">Nothing closed yet.</p>
          )}
        </Card>

        <Card title="Most common objections">
          {winLoss.data?.top_objections.length ? (
            <ul className="space-y-1.5">
              {winLoss.data.top_objections.map((objection) => (
                <li key={objection.objection} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-ink-700">{objection.objection}</span>
                  <Badge tone="amber">{objection.count}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">
              These come from Claude's reply analysis — they appear once replies start landing.
            </p>
          )}
        </Card>
      </div>

      <Card title="Recent win / loss reasons" className="mt-4" bodyClassName="p-0">
        {winLoss.data?.reasons.length ? (
          <ul className="divide-y divide-ink-100">
            {winLoss.data.reasons.map((reason, index) => (
              <li key={index} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                <StatusBadge status={reason.outcome} />
                <span className="font-medium text-ink-900">{reason.prospect}</span>
                <span className="text-ink-600">{reason.reason}</span>
                <span className="ml-auto text-xs text-ink-400">{date(reason.closed_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-ink-500">No closed deals recorded yet.</p>
        )}
      </Card>
    </>
  );
}
